import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Rocket, BookOpen, Compass } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { detectProfile } from "../lib/helpData";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const PROFILE_LABEL = {
  direccion: "Dirección / Gerencia",
  vendedor: "Vendedor",
  mensajero: "Mensajero",
  admin_fin: "Auxiliar Administrativo / Financiero",
  general: null,
};

// Mini-tour de bienvenida: se muestra UNA vez por colaborador (por dispositivo) y
// lo lleva a "Primeros pasos" del Centro de Ayuda, resaltando su perfil.
export default function WelcomeTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    if (location.pathname !== "/") return; // solo en el Panel Principal
    const key = `selec_welcome_seen_${user.id}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => {
        // No abrir si ya hay otro diálogo abierto (evita bloquear formularios).
        if (document.querySelector('[role="dialog"]')) return;
        setOpen(true);
      }, 2000); // tras desaparecer el splash de arranque
      return () => clearTimeout(t);
    }
  }, [user, location.pathname]);

  const dismiss = () => {
    if (user?.id) localStorage.setItem(`selec_welcome_seen_${user.id}`, "1");
    setOpen(false);
  };
  const goToGuide = () => { dismiss(); navigate("/ayuda"); };

  const profileLabel = PROFILE_LABEL[detectProfile(user)];
  const firstName = (user?.name || "").split(" ")[0];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="welcome-tour-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Rocket className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="font-heading text-center text-xl">
            {firstName ? `¡Te damos la bienvenida, ${firstName}!` : "¡Te damos la bienvenida!"}
          </DialogTitle>
          <DialogDescription className="text-center">
            ¿Primera vez por aquí? En pocos minutos te mostramos cómo empezar a usar el sistema
            {profileLabel ? <> — con una guía pensada para tu rol de <span className="font-semibold text-foreground">{profileLabel}</span></> : null}. Aprende por tu cuenta, sin que nadie tenga que explicarte.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-sm border border-border bg-muted/40 p-3 text-sm text-muted-foreground space-y-1.5">
          <p className="flex items-start gap-2"><BookOpen className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Encontrarás <span className="font-semibold text-foreground">Primeros pasos</span>, <span className="font-semibold text-foreground">Flujos de trabajo</span> y una guía por cada módulo.</p>
          <p className="flex items-start gap-2"><Compass className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Dentro de cada pantalla, el botón <span className="font-semibold text-foreground">"¿Cómo usar esta pantalla?"</span> te ayuda al instante.</p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="rounded-sm w-full sm:w-auto" onClick={dismiss} data-testid="welcome-skip-btn">Explorar por mi cuenta</Button>
          <Button className="rounded-sm font-bold w-full sm:w-auto gap-2" onClick={goToGuide} data-testid="welcome-start-btn">
            <BookOpen className="h-4 w-4" /> Ver primeros pasos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
